/**
 * SAT HPA™ · Block 11.2 · Assessment Components
 *
 * Kleine, komponierbare UI-Bausteine für die Assessment-Journey.
 * Keine fachliche HPA-Logik. Keine Scoring-Engine.
 */
import React from "react";
import { Link } from "react-router-dom";

// ============================================================================
// Phase-Label-Mapping (§ 7 · kundenfreundlich, keine technischen Enum-Werte)
// ============================================================================
export const PHASE_LABELS = {
  POTENTIAL_INDICATOR:  { num: "01", label: "Dein Potenzial" },
  EXPRESSION_INDICATOR: { num: "02", label: "Dein Alltag" },
  CONTRAST_INDICATOR:   { num: "03", label: "Deine Nuancen" },
  CONTEXT_INDICATOR:    { num: "04", label: "Deine Lebensbereiche" },
};

export const PHASE_ORDER = [
  "POTENTIAL_INDICATOR",
  "EXPRESSION_INDICATOR",
  "CONTRAST_INDICATOR",
  "CONTEXT_INDICATOR",
];

export function phaseLabelFor(phase) {
  return PHASE_LABELS[phase] || { num: "•", label: "Deine HPA" };
}

// ============================================================================
// HpaJourneyShell · gemeinsame Bühne für alle Assessment-Screens
// ============================================================================
export function HpaJourneyShell({ children, testid = "hpa-shell", pulsingThread = false }) {
  return (
    <div className="hpa hpa-canvas hpa-safe-bottom" data-testid={testid}>
      <div
        className={`container-sat max-w-3xl pt-24 md:pt-28 pb-16 hpa-thread ${
          pulsingThread ? "hpa-thread-pulsing" : ""
        }`}
      >
        <div className="pl-6 md:pl-10">{children}</div>
      </div>
    </div>
  );
}

// ============================================================================
// HpaPhaseIndicator (§ 7, § 18)
// ============================================================================
export function HpaPhaseIndicator({ currentPhase }) {
  const currentIdx = PHASE_ORDER.indexOf(currentPhase);
  return (
    <ul className="hpa-phase-list" data-testid="hpa-phase-indicator">
      {PHASE_ORDER.map((p, i) => {
        const state =
          i < currentIdx ? "done" : i === currentIdx ? "current" : "todo";
        const { num, label } = PHASE_LABELS[p];
        return (
          <li
            key={p}
            className="hpa-phase-item"
            data-state={state}
            data-testid={`hpa-phase-${state}-${p}`}
          >
            <span className="hpa-phase-dot" />
            <span>
              {num} · {label}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// ============================================================================
// HpaProgress (§ 11, 17) — nur so viel wie das Backend zuverlässig liefert
// ============================================================================
export function HpaProgress({ phase, completedItems }) {
  const currentIdx = Math.max(0, PHASE_ORDER.indexOf(phase));
  const total = PHASE_ORDER.length;
  return (
    <div
      className="text-[color:var(--hpa-text-mute)] text-xs uppercase tracking-[0.24em] font-medium"
      data-testid="hpa-progress"
    >
      Phase {currentIdx + 1} von {total}
      {typeof completedItems === "number" && completedItems > 0 && (
        <span className="ml-4 text-[color:var(--hpa-text-mute)]/70">
          · {completedItems} Fragen beantwortet
        </span>
      )}
    </div>
  );
}

// ============================================================================
// HpaSaveState (§ 14)
// ============================================================================
export function HpaSaveState({ state = "idle" }) {
  if (state === "idle") return null;
  const label =
    state === "saving"
      ? "Wird gespeichert"
      : state === "saved"
      ? "Gespeichert"
      : "Fehler beim Speichern";
  return (
    <div
      className="hpa-save-state"
      data-state={state}
      data-testid={`hpa-save-state-${state}`}
      aria-live="polite"
    >
      <span className="hpa-save-dot" aria-hidden="true" />
      {label}
    </div>
  );
}

// ============================================================================
// HpaAnswerOption
// ============================================================================
export function HpaAnswerOption({
  option,
  onSelect,
  selecting = false,
  disabled = false,
  testid,
}) {
  const handleClick = () => {
    if (disabled) return;
    onSelect(option);
  };
  const handleKey = (e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(option);
    }
  };
  return (
    <button
      type="button"
      className="hpa-answer-option"
      onClick={handleClick}
      onKeyDown={handleKey}
      disabled={disabled}
      data-selecting={selecting ? "true" : "false"}
      aria-pressed={selecting}
      data-testid={testid || `hpa-option-${option.option_id}`}
    >
      {option.label_de}
    </button>
  );
}

// ============================================================================
// HpaQuestionCard
// ============================================================================
export function HpaQuestionCard({
  question,
  onAnswer,
  selectingOptionId,
  disabled,
  saveState,
}) {
  return (
    <div data-testid="hpa-question-card">
      <h1
        className="hpa-question-prompt mb-10"
        tabIndex={-1}
        ref={(el) => {
          // Focus Management (§ 37): nach Wechsel Focus auf Prompt
          if (el && document.activeElement !== el) {
            try {
              el.focus({ preventScroll: false });
            } catch (_e) {
              /* noop */
            }
          }
        }}
        data-testid="hpa-question-prompt"
      >
        {question.prompt_de}
      </h1>

      <div className="flex flex-col gap-3 max-w-2xl">
        {question.response_options.map((opt) => (
          <HpaAnswerOption
            key={opt.option_id}
            option={opt}
            onSelect={onAnswer}
            selecting={selectingOptionId === opt.option_id}
            disabled={disabled}
          />
        ))}
      </div>

      <div className="mt-10">
        <HpaSaveState state={saveState} />
      </div>
    </div>
  );
}

// ============================================================================
// HpaPauseAction (§ 19)
// ============================================================================
export function HpaPauseAction({ onPause, disabled }) {
  return (
    <button
      type="button"
      className="hpa-pause-action"
      onClick={onPause}
      disabled={disabled}
      data-testid="hpa-pause-action"
    >
      Für später speichern
    </button>
  );
}

// ============================================================================
// HpaErrorState — recoverable
// ============================================================================
export function HpaErrorState({ title, message, cta = "Erneut versuchen", onRetry, testid }) {
  return (
    <div className="hpa-error-card" data-testid={testid || "hpa-error"}>
      <div className="hpa-error-title">{title || "Kurze Unterbrechung"}</div>
      <div className="text-[15px] leading-relaxed mb-4 text-[color:var(--hpa-text-soft)]">
        {message}
      </div>
      {onRetry && (
        <button
          type="button"
          className="hpa-btn-ghost"
          onClick={onRetry}
          data-testid={`${testid || "hpa-error"}-retry`}
        >
          {cta}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// HpaSynthesisState (§ 28, § 29)
// ============================================================================
const SYNTHESIS_LINES = [
  "Deine Antworten werden zusammengeführt.",
  "Potenzial und aktueller Ausdruck werden betrachtet.",
  "Spannungsfelder werden sichtbar.",
  "Dein persönliches HPA-Profil entsteht.",
];

export function HpaSynthesisState({ activeIndex = 0 }) {
  return (
    <div className="hpa-synthesis-lines" data-testid="hpa-synthesis-lines">
      {SYNTHESIS_LINES.map((line, idx) => {
        const active = idx === activeIndex;
        const passed = idx < activeIndex;
        return (
          <div
            key={idx}
            className="hpa-synthesis-line"
            data-active={active ? "true" : "false"}
            data-passed={passed ? "true" : "false"}
            data-testid={`hpa-synthesis-line-${idx}`}
          >
            {line}
          </div>
        );
      })}
    </div>
  );
}
export const SYNTHESIS_LINE_COUNT = SYNTHESIS_LINES.length;

// ============================================================================
// HpaTopBar — kleine Meta-Bar mit Phase + Pause
// ============================================================================
export function HpaTopBar({ phase, completedItems, onPause, pauseDisabled }) {
  return (
    <div className="flex items-center justify-between mb-12" data-testid="hpa-topbar">
      <div>
        <div className="hpa-overline mb-2">SAT HPA™</div>
        <HpaProgress phase={phase} completedItems={completedItems} />
      </div>
      {onPause && (
        <HpaPauseAction onPause={onPause} disabled={pauseDisabled} />
      )}
    </div>
  );
}

// ============================================================================
// HpaBackToHome — sekundärer Ausweg
// ============================================================================
export function HpaBackToHome() {
  return (
    <Link
      to="/hpa"
      className="hpa-btn-ghost"
      data-testid="hpa-back-to-home"
    >
      Zur HPA-Übersicht
    </Link>
  );
}
